/**
 * Echo API Functions
 * Hooks for interacting with Echo backend endpoints
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiData, useAuthenticatedFetch } from './client';
import type {
  Matter,
  EchoMatter,
  EchoMatterCreate,
  EchoMatterUpdate,
  EchoMatterVideoStats,
  EchoStreamer,
  EchoStreamerCreate,
  EchoStreamerVideoStats,
  EchoRun,
  EchoRunCreate,
  EchoRunUpdate,
  EchoUser,
  EchoUserCreate,
  EchoUserUpdate,
  UserMatterAssignmentsResponse,
  AccessibleRunsResponse,
} from './types/echo';

// ============================================================================
// Matters API (main database)
// ============================================================================

/**
 * Fetch all matters from main database
 */
export function useMatters(limit = 100) {
  return useApiData<Matter[]>(`/matters/?limit=${limit}`);
}

// ============================================================================
// Echo Matters API
// ============================================================================

/**
 * Fetch all Echo-registered matters (unfiltered — use for admin pages)
 */
export function useEchoMatters(limit = 100) {
  return useApiData<EchoMatter[]>(`/echo-matters/?limit=${limit}`);
}

/**
 * Fetch matters assigned to the current user.
 * Returns all matters for admin users, only assigned matters for standard users.
 * Use this for user-facing dropdowns (Transcribe, Upload pages).
 */
export function useMyMatters() {
  return useApiData<EchoMatter[]>('/echo-matters/my');
}

/**
 * Fetch single Echo matter by ID
 */
export function useEchoMatter(id: number | null) {
  return useApiData<EchoMatter>(id ? `/echo-matters/${id}` : '');
}

/**
 * Fetch video statistics for an Echo matter
 */
export function useEchoMatterVideoStats(id: number | null) {
  return useApiData<EchoMatterVideoStats>(
    id ? `/echo-matters/${id}/video-stats` : ''
  );
}

/**
 * Hook for Echo matter mutations (create, update)
 */
export function useEchoMatterMutations() {
  const { fetchData } = useAuthenticatedFetch<EchoMatter>();

  const createEchoMatter = useCallback(
    async (data: EchoMatterCreate) => {
      return fetchData('/echo-matters/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  const updateEchoMatter = useCallback(
    async (id: number, data: EchoMatterUpdate) => {
      return fetchData(`/echo-matters/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  return { createEchoMatter, updateEchoMatter };
}

// ============================================================================
// Echo Streamers API
// ============================================================================

/**
 * Fetch all streamers (no matter filter)
 */
export function useAllEchoStreamers(limit = 500) {
  return useApiData<EchoStreamer[]>(`/echo-streamers/?limit=${limit}`);
}

/**
 * Fetch streamers for a specific Echo matter
 */
export function useEchoStreamers(echoMatterId: number | null, limit = 100) {
  return useApiData<EchoStreamer[]>(
    echoMatterId
      ? `/echo-streamers/?under_echo_matter_id=${echoMatterId}&limit=${limit}`
      : ''
  );
}

/**
 * Fetch video statistics for a streamer
 */
export function useEchoStreamerVideoStats(id: number | null) {
  return useApiData<EchoStreamerVideoStats>(
    id ? `/echo-streamers/${id}/video-stats` : ''
  );
}

/**
 * Hook for Echo streamer mutations
 */
export function useEchoStreamerMutations() {
  const { fetchData } = useAuthenticatedFetch<EchoStreamer>();

  const createStreamer = useCallback(
    async (data: EchoStreamerCreate) => {
      return fetchData('/echo-streamers/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  const findOrCreateStreamer = useCallback(
    async (
      echoMatterId: number,
      name: string,
      platform: 'kick' | 'youtube' | 'twitch' | 'apple-podcasts' | 'gettr' | 'upload'
    ) => {
      // First, try to find existing streamer
      const existingResponse = await fetchData(
        `/echo-streamers/?under_echo_matter_id=${echoMatterId}&limit=100`
      );

      if (existingResponse && Array.isArray(existingResponse)) {
        const streamers = existingResponse as unknown as EchoStreamer[];
        const existing = streamers.find(
          (s) =>
            s.name.toLowerCase() === name.toLowerCase() &&
            s.platform === platform
        );
        if (existing) {
          return existing;
        }
      }

      // Create new streamer
      return createStreamer({
        id_echo_matter: echoMatterId,
        name,
        platform,
      });
    },
    [fetchData, createStreamer]
  );

  return { createStreamer, findOrCreateStreamer };
}

// ============================================================================
// Echo Runs API
// ============================================================================

/**
 * Fetch echo runs with optional filtering
 */
export function useEchoRuns(options?: {
  echoStreamerId?: number;
  mine?: boolean;
  skip?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
}) {
  const params = new URLSearchParams();
  if (options?.echoStreamerId) {
    params.set('echo_streamer_id', String(options.echoStreamerId));
  }
  if (options?.mine) {
    params.set('mine', 'true');
  }
  if (options?.skip) {
    params.set('skip', String(options.skip));
  }
  params.set('limit', String(options?.limit ?? 50));
  params.set('order_by', options?.orderBy ?? 'created_at');
  params.set('ascending', String(options?.ascending ?? false));

  return useApiData<EchoRun[]>(`/echo-runs/?${params.toString()}`);
}

/**
 * Total number of runs visible to the caller under the same filters as
 * useEchoRuns. Used to show "N of M" alongside the run list.
 */
export function useEchoRunsCount(options?: {
  echoStreamerId?: number;
  mine?: boolean;
}) {
  const params = new URLSearchParams();
  if (options?.echoStreamerId) {
    params.set('echo_streamer_id', String(options.echoStreamerId));
  }
  if (options?.mine) {
    params.set('mine', 'true');
  }
  const qs = params.toString();
  return useApiData<{ total: number }>(
    qs ? `/echo-runs/count?${qs}` : '/echo-runs/count'
  );
}

/**
 * Paginated run list with a "load more" hook. Fetches the first page on
 * mount; call loadMore() to append the next page. `filters` should be
 * memoized by the caller — changing it resets the list.
 */
export interface UseEchoRunsPaginatedOptions {
  mine?: boolean;
  pageSize?: number;
}

export function useEchoRunsPaginated(options: UseEchoRunsPaginatedOptions = {}) {
  const { mine = false, pageSize = 50 } = options;

  // `fetchData` from useAuthenticatedFetch changes identity on every render,
  // so it is captured in a ref rather than an effect/callback dep — otherwise
  // the initial-page effect would re-fire indefinitely and exhaust browser
  // socket resources.
  const { fetchData } = useAuthenticatedFetch<EchoRun[]>();
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  const [runs, setRuns] = useState<EchoRun[]>([]);
  const runsRef = useRef<EchoRun[]>([]);
  runsRef.current = runs;

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [error, setError] = useState<Error | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const reachedEndRef = useRef(false);

  const buildUrl = useCallback(
    (skip: number) => {
      const params = new URLSearchParams();
      if (mine) params.set('mine', 'true');
      params.set('skip', String(skip));
      params.set('limit', String(pageSize));
      params.set('order_by', 'created_at');
      params.set('ascending', 'false');
      return `/echo-runs/?${params.toString()}`;
    },
    [mine, pageSize]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRuns([]);
    runsRef.current = [];
    setReachedEnd(false);
    reachedEndRef.current = false;

    fetchRef.current(buildUrl(0))
      .then((page) => {
        if (cancelled) return;
        const rows = page ?? [];
        setRuns(rows);
        runsRef.current = rows;
        const done = rows.length < pageSize;
        setReachedEnd(done);
        reachedEndRef.current = done;
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buildUrl, pageSize]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || reachedEndRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchRef.current(buildUrl(runsRef.current.length));
      const rows = page ?? [];
      const next = [...runsRef.current, ...rows];
      setRuns(next);
      runsRef.current = next;
      if (rows.length < pageSize) {
        setReachedEnd(true);
        reachedEndRef.current = true;
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [buildUrl, pageSize]);

  return { runs, loading, loadingMore, error, reachedEnd, loadMore };
}

/**
 * Aggregated billing summary — one row per (matter, streamer) with completed-run totals.
 * Server-side aggregation; does not paginate.
 */
export interface BillingSummaryRow {
  echo_matter_id: number;
  mattercode: string;
  matter_name: string;
  billing_rate: number;
  echo_streamer_id: number;
  streamer_name: string;
  video_count: number;
  total_minutes: number;
}

export function useEchoBillingSummary() {
  return useApiData<BillingSummaryRow[]>('/echo-runs/billing-summary');
}

/**
 * Fetch single echo run by ID
 */
export function useEchoRun(runId: number | null) {
  return useApiData<EchoRun>(runId ? `/echo-runs/${runId}` : '');
}

/**
 * Hook for Echo run mutations
 */
export function useEchoRunMutations() {
  const { fetchData } = useAuthenticatedFetch<EchoRun>();

  const createEchoRun = useCallback(
    async (data: EchoRunCreate) => {
      return fetchData('/echo-runs/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  const updateEchoRun = useCallback(
    async (runId: number, data: EchoRunUpdate) => {
      return fetchData(`/echo-runs/${runId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  return { createEchoRun, updateEchoRun };
}

// ============================================================================
// Echo Users API
// ============================================================================

/**
 * Fetch all Echo users (admin only)
 */
export function useEchoUsers(skip = 0, limit = 100) {
  return useApiData<EchoUser[]>(`/echo-users/?skip=${skip}&limit=${limit}`);
}

/**
 * Fetch Echo user by Cognito sub
 */
export function useEchoUserBySub(cognitoSub: string | null) {
  return useApiData<EchoUser>(
    cognitoSub ? `/echo-users/by-sub/${cognitoSub}` : ''
  );
}

/**
 * Hook for Echo user mutations
 */
export function useEchoUserMutations() {
  const { fetchData } = useAuthenticatedFetch<EchoUser>();
  const { fetchData: deleteData } = useAuthenticatedFetch<void>();

  const createEchoUser = useCallback(
    async (data: EchoUserCreate) => {
      return fetchData('/echo-users/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  const updateEchoUser = useCallback(
    async (userId: number, data: EchoUserUpdate) => {
      return fetchData(`/echo-users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    [fetchData]
  );

  const deleteEchoUser = useCallback(
    async (userId: number) => {
      return deleteData(`/echo-users/${userId}`, {
        method: 'DELETE',
      });
    },
    [deleteData]
  );

  const getOrCreateEchoUser = useCallback(
    async (cognitoSub: string, email: string) => {
      // Try to get existing user
      try {
        const existing = await fetchData(`/echo-users/by-sub/${cognitoSub}`);
        if (existing) {
          return existing;
        }
      } catch {
        // User doesn't exist, create new one
      }

      // Create new user
      return createEchoUser({ cognito_sub: cognitoSub, email });
    },
    [fetchData, createEchoUser]
  );

  return {
    createEchoUser,
    updateEchoUser,
    deleteEchoUser,
    getOrCreateEchoUser,
  };
}

// ============================================================================
// Matter Assignment API (admin only)
// ============================================================================

/**
 * Fetch matter assignments for a specific user (admin only).
 */
export function useUserMatterAssignments(userId: number | null) {
  return useApiData<UserMatterAssignmentsResponse>(
    userId ? `/echo-users/${userId}/matter-assignments` : ''
  );
}

/**
 * Hook for managing matter assignments (admin only).
 * Provides updateAssignments to bulk-replace a user's matter assignments.
 */
export function useMatterAssignmentMutations() {
  const { fetchData } = useAuthenticatedFetch<UserMatterAssignmentsResponse>();

  const updateAssignments = useCallback(
    async (userId: number, echoMatterIds: number[]) => {
      return fetchData(`/echo-users/${userId}/matter-assignments`, {
        method: 'PUT',
        body: JSON.stringify({ echo_matter_ids: echoMatterIds }),
      });
    },
    [fetchData]
  );

  return { updateAssignments };
}

// ============================================================================
// Accessible Runs API (matter-filtered content navigation)
// ============================================================================

/**
 * Fetch runs accessible to the current user, grouped hierarchically.
 * Returns all runs for admin users, only matter-filtered runs for standard users.
 * Used by the View Transcriptions page for DB-driven navigation.
 */
export function useAccessibleRuns() {
  return useApiData<AccessibleRunsResponse>('/echo/accessible-runs');
}
